import { signal } from "../net/signal";

/**
 * سرعتِ صحنه — همان `Speed` کلاینتِ یونیتی، با همان پله‌ها و همان پایداری.
 *
 * یونیتی چهار چیز دارد که وب نداشت: پله‌های نام‌دار، ذخیره‌ی انتخاب، دکمه‌ی
 * چرخاندن، و پیش‌فرضِ «آرام». وب فقط عددِ ۱٫۷ را ثابت گذاشته بود — و چون
 * ضریب در CSS از `var(--k)` می‌آید، هر شیوه‌نامه‌ای که پیش از اولین ایمپورتِ
 * این فایل کشیده می‌شد با پیش‌فرضِ ۱ می‌رفت، یعنی ۷۰٪ تندتر.
 *
 * یک جا برای همه: تایمرهای JS از `ms()` می‌گذرند و انیمیشن‌های CSS از
 * `var(--k)` که همین‌جا روی <html> نوشته می‌شود — و حالا سرِ بالاآمدنِ اپ، نه
 * سرِ اولین ایمپورتِ تصادفی.
 */
export type SpeedName = "خیلی آرام" | "آرام" | "عادی" | "سریع";

/** پله‌ها. سه‌تای آخر همان عددهای Speed.K در یونیتی‌اند. */
export const STEPS: { name: SpeedName; k: number }[] = [
  { name: "خیلی آرام", k: 2.6 },
  { name: "آرام", k: 1.7 },
  { name: "عادی", k: 1 },
  { name: "سریع", k: 0.55 },
];

const KEY = "bazivan.speed";

function load(): number {
  try {
    const v = Number(localStorage.getItem(KEY));
    if (STEPS.some((s) => s.k === v)) return v;
  } catch { /* حالتِ ناشناس */ }
  return STEPS[0]!.k;   // پیش‌فرض: کندترین پله
}

export const speed = signal<number>(load());

/**
 * ضریبِ جبرانِ عقب‌ماندگی — ۱ یعنی سرِ وقتیم، کمتر یعنی داریم می‌دویم.
 *
 * سرور دیگر آهنگِ صحنه را دیکته نمی‌کند: رویداد را همان لحظه می‌فرستد و
 * آهستگی و طبیعی‌بودن کارِ همین‌جاست. ولی «مالِ خودت است» یعنی مسئولیتش هم
 * مالِ خودت: اگر جا ماندی، باید جمعش کنی. این ضریب همان جمع‌کردن است —
 * انیمیشن‌ها نرم فشرده می‌شوند تا صف برسد، به‌جای این‌که عقب‌ماندگی روی هم
 * بنشیند (که تماشاچی‌ها را دقیقه‌ها عقب می‌انداخت).
 */
let catchUp = 1;

/** ضریبِ جاری — انتخابِ بازیکن ضربدرِ جبران. تایمرهای JS این را می‌خوانند. */
export let K = speed.value;

/**
 * جبران را تنظیم کن. صفِ صحنه صدایش می‌زند؛ جای دیگری نه.
 *
 * چرا این‌جا و نه در CSS: `--k` تنها جایی است که هم تایمرهای JS و هم
 * انیمیشن‌های CSS از آن می‌گذرند. با نوشتنِ ضریبِ *مؤثر* روی همان، هیچ
 * شیوه‌نامه‌ای لازم نیست عوض شود.
 */
export function setCatchUp(f: number): void {
  const c = Math.min(1, Math.max(0.25, f));
  if (Math.abs(c - catchUp) < 0.02) return;   // لرزشِ بی‌فایده روی <html>
  catchUp = c;
  apply(speed.value);
}

export const ms = (x: number): number => Math.round(x * K);

export function speedName(): SpeedName {
  // نامِ پله‌ی *انتخابیِ* بازیکن، نه ضریبِ مؤثر: جبران موقتی است و نباید
  // برچسبِ دکمه را عوض کند.
  return (STEPS.find((s) => s.k === speed.value) ?? STEPS[0]!).name;
}

/** پله‌ی بعدی — همان Speed.Cycle در یونیتی. */
export function cycleSpeed(): void {
  const at = STEPS.findIndex((s) => s.k === speed.value);
  apply(STEPS[(at + 1) % STEPS.length]!.k);
}

/** یک پله‌ی مشخص — برگه‌ی ⚙ ِ میز همه‌ی پله‌ها را کنارِ هم نشان می‌دهد. عددِ بیرون از STEPS نادیده است. */
export function setSpeed(k: number): void {
  if (STEPS.some((s) => s.k === k)) apply(k);
}

function apply(k: number): void {
  K = k * catchUp;
  speed.value = k;
  try { localStorage.setItem(KEY, String(k)); } catch { /* حالتِ ناشناس */ }
  if (typeof document !== "undefined")
    document.documentElement.style.setProperty("--k", String(K));
}

apply(K);
